import React, { useState, useEffect } from 'react';
import { useTheme, getT, getBrandColors} from '../ThemeContext';
import PageHeaderIcon from "../components/PageHeaderIcon";
import API_BASE from '../apiConfig';
import { getSessionUser } from '../utils/session';
import { FaClipboardCheck, FaCheckCircle, FaExclamationCircle, FaDollarSign, FaBan, FaUndo, FaHandshake, FaStickyNote } from 'react-icons/fa';

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";
const BLUR = "blur(18px)";

function SortTh({ label, col, sortKey, sortDir, onSort, align = 'left' }) {
  const active = sortKey === col;
  return (
    <th onClick={() => onSort(col)} style={{
      padding: '11px 14px', fontWeight: 800, fontSize: 10, textTransform: 'uppercase',
      letterSpacing: '0.08em', textAlign: align, color: active ? getBrandColors().yellow : undefined,
      cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontFamily: FONT,
    }}>
      {label}
      <span style={{ marginLeft: 4, opacity: active ? 1 : 0.3, fontSize: 10 }}>
        {active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    </th>
  );
}

const STATUS_COLORS = {
  REGISTERED: { bg: 'rgba(156,163,175,0.12)', text: '#9ca3af', border: 'rgba(156,163,175,0.30)' },
  APPROVED: { bg: 'rgba(96,165,250,0.12)', text: '#60a5fa', border: 'rgba(96,165,250,0.30)' },
  PAID: { bg: 'rgba(52,211,153,0.12)', text: getBrandColors().green, border: 'rgba(52,211,153,0.30)' },
  VOIDED: { bg: 'rgba(248,113,113,0.12)', text: getBrandColors().red, border: 'rgba(248,113,113,0.30)' },
  CANCELLED: { bg: 'rgba(248,113,113,0.12)', text: getBrandColors().red, border: 'rgba(248,113,113,0.30)' },
};

const TournamentAdminApproval = ({ embedded = false, extTournamentId = '', extAgeCategoryId = '' } = {}) => {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const user = getSessionUser();

  const [tournaments, setTournaments] = useState([]);
  const [tournamentId, setTournamentId] = useState('');
  const [tournament, setTournament] = useState(null);
  const [ageCategoryId, setAgeCategoryId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [registrations, setRegistrations] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState(null);

  const [sortKey, setSortKey] = useState('registration_date');
  const [sortDir, setSortDir] = useState('desc');

  const [refundModal, setRefundModal] = useState(null); // { registration_id, total_cost, refunded_amount }
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundType, setRefundType] = useState('full');

  const [ackModal, setAckModal] = useState(null); // registration row for offline acknowledgement
  const [ackNotes, setAckNotes] = useState('');

  useEffect(() => { loadTournaments(); }, []);

  const loadTournaments = async () => {
    try {
      const res = await fetch(`${BASE}/tournament`);
      const data = await res.json();
      if (data.success) setTournaments(data.tournaments || []);
    } catch (err) { console.error(err); }
  };

  const loadTournamentDetail = async (id) => {
    if (!id) { setTournament(null); return; }
    const res = await fetch(`${BASE}/tournament/${id}`);
    const data = await res.json();
    if (data.success) setTournament(data);
  };

  const loadRegistrations = async (id, catId, from, to) => {
    if (!id) { setRegistrations([]); return; }
    setError('');
    try {
      const params = new URLSearchParams();
      if (catId) params.set('age_category_id', catId);
      if (from) params.set('date_from', from);
      if (to) params.set('date_to', to);
      const res = await fetch(`${BASE}/tournament/${id}/approval?${params.toString()}`);
      const data = await res.json();
      if (data.success) setRegistrations(data.registrations || []);
      else setError(data.message || 'Failed to load registrations');
    } catch (err) { setError(err.message); }
  };

  const onTournamentChange = (id) => {
    setTournamentId(id);
    setAgeCategoryId(''); setDateFrom(''); setDateTo('');
    loadTournamentDetail(id);
    loadRegistrations(id, '', '', '');
  };

  // Embedded (hub) mode: one coherent load on mount and on any shared-filter
  // change — avoids the two-effect race that left tab content stale.
  useEffect(() => {
    if (!embedded) return;
    if (!extTournamentId) { setTournamentId(''); setRegistrations([]); return; }
    setTournamentId(String(extTournamentId));
    setAgeCategoryId(extAgeCategoryId ? String(extAgeCategoryId) : '');
    loadTournamentDetail(extTournamentId);
    loadRegistrations(extTournamentId, extAgeCategoryId || '', '', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, extTournamentId, extAgeCategoryId]);

  const applyFilters = () => loadRegistrations(tournamentId, ageCategoryId, dateFrom, dateTo);

  const handleSort = (col) => {
    setSortDir(prev => (sortKey === col ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(col);
  };

  const sortedRows = [...registrations].sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'string') { av = (av || '').toLowerCase(); bv = (bv || '').toLowerCase(); }
    if (av === undefined || av === null) av = '';
    if (bv === undefined || bv === null) bv = '';
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const runAction = async (endpoint, registrationId, body = {}) => {
    setBusyId(registrationId); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE}/tournament/registrations/${registrationId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id, ...body }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(data.message || 'Action completed');
        loadRegistrations(tournamentId, ageCategoryId, dateFrom, dateTo);
      } else {
        setError(data.message || data.detail || 'Action failed');
      }
    } catch (err) { setError(err.message); }
    finally { setBusyId(null); }
  };

  const openRefundModal = (reg) => {
    setRefundModal(reg);
    setRefundAmount('');
    setRefundReason('');
    setRefundType('full');
  };

  const submitRefund = async () => {
    if (!refundModal) return;
    await runAction('refund', refundModal.registration_id, {
      refund_type: refundType,
      amount: refundType === 'partial' ? parseFloat(refundAmount) : undefined,
      reason: refundReason,
    });
    setRefundModal(null);
  };

  const openAckModal = (reg) => { setAckModal(reg); setAckNotes(reg.admin_notes || ''); };

  // Offline acknowledgement: record notes + approve on the parent's behalf, and
  // (optionally) charge the card on file — all in ONE request. The row updates
  // optimistically so the UI reflects the result immediately.
  const patchRow = (regId, changes) =>
    setRegistrations(prev => prev.map(r => r.registration_id === regId ? { ...r, ...changes } : r));

  const submitAck = async (alsoPay) => {
    if (!ackModal) return;
    const regId = ackModal.registration_id;
    setBusyId(regId); setError(''); setSuccess('');
    // Optimistic: show the intended state right away.
    patchRow(regId, { admin_notes: ackNotes || null,
      ...(ackModal.registration_status === 'REGISTERED' ? { registration_status: alsoPay ? 'PAID' : 'APPROVED' } : {}),
      ...(alsoPay ? { payment_status: 'COMPLETED' } : {}) });
    try {
      const res = await fetch(`${BASE}/tournament/registrations/${regId}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id, notes: ackNotes, charge: alsoPay }),
      });
      const data = await res.json();
      if (data.success) setSuccess(data.message || 'Done');
      else setError(data.message || data.detail || 'Action failed');
      // Reconcile with the server in the background (cheap now that DB is fast).
      loadRegistrations(tournamentId, ageCategoryId, dateFrom, dateTo);
    } catch (err) { setError(err.message); loadRegistrations(tournamentId, ageCategoryId, dateFrom, dateTo); }
    finally { setBusyId(null); setAckModal(null); }
  };

  const glassCard = {
    background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
    border: T.glassBdr, borderRadius: '16px', boxShadow: T.glassSh,
    padding: '24px', boxSizing: 'border-box', marginBottom: '20px',
  };
  const labelStyle = {
    display: 'block', marginBottom: '8px', fontSize: '11px', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textLabel, fontFamily: FONT,
  };
  const inputStyle = {
    border: `1.5px solid ${T.inputBdr}`, borderRadius: '10px', padding: '10px 14px',
    background: T.inputBg, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
    width: '100%', boxSizing: 'border-box', outline: 'none', fontSize: '14px',
    color: T.inputText, fontFamily: FONT,
  };
  const actionBtn = (color) => ({
    padding: '6px 10px', border: `1px solid ${color}55`, borderRadius: '6px',
    background: `${color}18`, color, cursor: 'pointer', fontSize: '11px', fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', gap: '4px', fontFamily: FONT, marginRight: '6px',
  });

  return (
    <div style={embedded ? { padding: 0, boxSizing: 'border-box' } : { minHeight: '100dvh', padding: '20px 28px 48px', boxSizing: 'border-box' }}>
      {!embedded && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
        <PageHeaderIcon size={48} icon={<FaClipboardCheck size={22} color={getBrandColors().blueDark} />} />
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 800, color: T.textPrimary, margin: 0, fontFamily: FONT }}>
            Tournament Admin Approval
          </h1>
          <p style={{ fontSize: '13px', color: T.textMuted, margin: '4px 0 0', fontFamily: FONT }}>
            Review registrations — pay, void, or refund
          </p>
        </div>
      </div>
      )}

      {error && (
        <div style={{ display: 'flex', gap: '10px', padding: '12px 16px', borderRadius: '10px', marginBottom: '18px',
          fontSize: '13px', fontWeight: 600, fontFamily: FONT, background: 'rgba(248,113,113,0.12)', color: getBrandColors().red,
          border: '1px solid rgba(248,113,113,0.30)' }}>
          <FaExclamationCircle size={16} /><span>{error}</span>
        </div>
      )}
      {success && (
        <div style={{ display: 'flex', gap: '10px', padding: '12px 16px', borderRadius: '10px', marginBottom: '18px',
          fontSize: '13px', fontWeight: 600, fontFamily: FONT, background: 'rgba(52,211,153,0.12)', color: getBrandColors().green,
          border: '1px solid rgba(52,211,153,0.30)' }}>
          <FaCheckCircle size={16} /><span>{success}</span>
        </div>
      )}

      {!embedded && (
      <div style={glassCard}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Tournament</label>
            <select value={tournamentId} onChange={(e) => onTournamentChange(e.target.value)} style={inputStyle}>
              <option value="">— Choose —</option>
              {tournaments.map(t => (
                <option key={t.tournament_id} value={t.tournament_id}>{t.tournament_name} ({t.status})</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Age Category</label>
            <select value={ageCategoryId} onChange={(e) => setAgeCategoryId(e.target.value)} style={inputStyle} disabled={!tournament}>
              <option value="">All</option>
              {tournament?.age_categories.map(c => (
                <option key={c.age_category_id} value={c.age_category_id}>{c.category_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={inputStyle} />
          </div>
        </div>
        <button onClick={applyFilters} disabled={!tournamentId} style={{
          marginTop: '16px', padding: '9px 18px', borderRadius: '8px',
          background: T.glass, border: T.glassBdr, color: T.inputText, fontWeight: 700,
          fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
        }}>
          Apply Filters
        </button>
      </div>
      )}

      {tournamentId && (
        <div style={glassCard}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: FONT, color: T.textPrimary }}>
              <thead>
                <tr style={{ background: T.glass, borderBottom: `2px solid ${T.inputBdr}` }}>
                  <SortTh label="Student" col="first_name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <SortTh label="Category" col="age_code" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="center" />
                  <SortTh label="Parent" col="parent_name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <SortTh label="Contact" col="parent_phone" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  <th style={{ padding: '11px 14px', fontWeight: 800, fontSize: 10, textTransform: 'uppercase', fontFamily: FONT }}>Availability</th>
                  <SortTh label="Cost/Game" col="cost_per_game" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortTh label="Amount" col="total_cost" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortTh label="Refund" col="refunded_amount" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortTh label="Status" col="registration_status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="center" />
                  <SortTh label="Payment" col="payment_status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="center" />
                  <th style={{ padding: '11px 14px', fontWeight: 800, fontSize: 10, textTransform: 'uppercase', fontFamily: FONT }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, idx) => {
                  const sc = STATUS_COLORS[r.registration_status] || STATUS_COLORS.REGISTERED;
                  const pc = r.payment_status === 'COMPLETED' ? STATUS_COLORS.PAID
                    : r.payment_status === 'PARTIALLY_REFUNDED' ? { bg: 'rgba(251,191,36,0.12)', text: '#f59e0b', border: 'rgba(251,191,36,0.30)' }
                    : r.payment_status === 'REFUNDED' ? STATUS_COLORS.VOIDED
                    : STATUS_COLORS.REGISTERED;
                  const payLabel = r.payment_status === 'PARTIALLY_REFUNDED' ? 'Partial Refund' : r.payment_status;
                  // Refundable while any balance remains (total minus what's already refunded).
                  const refundable = (r.payment_status === 'COMPLETED' || r.payment_status === 'PARTIALLY_REFUNDED')
                    && (Number(r.total_cost) - Number(r.refunded_amount || 0)) > 0;
                  return (
                    <tr key={r.registration_id} style={{ background: idx % 2 === 0 ? 'transparent' : T.glass, borderBottom: `1px solid ${T.inputBdr}` }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                        {r.first_name} {r.last_name}
                        {r.admin_notes && (
                          <div style={{ marginTop: 3, fontSize: '10.5px', fontWeight: 600, color: T.textMuted, fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 4 }} title={r.admin_notes}>
                            <FaStickyNote size={9} /> {r.admin_notes.length > 40 ? r.admin_notes.slice(0, 40) + '…' : r.admin_notes}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>{r.age_code}</td>
                      <td style={{ padding: '10px 14px' }}>{r.parent_name}</td>
                      <td style={{ padding: '10px 14px' }}>{r.parent_phone}</td>
                      <td style={{ padding: '10px 14px', fontSize: '11px', color: T.textMuted }}>
                        {r.availability_dates.map(d => `Day ${d.day_number}`).join(', ')}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>${Number(r.cost_per_game).toFixed(2)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {(() => {
                          const total = Number(r.total_cost) || 0;
                          const refunded = Number(r.refunded_amount) || 0;
                          const net = Math.max(0, total - refunded);
                          return refunded > 0
                            ? (<><span style={{ textDecoration: 'line-through', color: T.textMuted, fontWeight: 500, marginRight: 6 }}>${total.toFixed(2)}</span><span>${net.toFixed(2)}</span></>)
                            : (<span>${total.toFixed(2)}</span>);
                        })()}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {Number(r.refunded_amount) > 0
                          ? <span style={{ color: '#ef4444', fontWeight: 700 }}>–${Number(r.refunded_amount).toFixed(2)}</span>
                          : <span style={{ color: T.textMuted }}>—</span>}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <span style={{ padding: '3px 9px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}>
                          {r.registration_status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <span style={{ padding: '3px 9px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, background: pc.bg, color: pc.text, border: `1px solid ${pc.border}` }}>
                          {payLabel}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        {r.registration_status === 'REGISTERED' && r.booked_by_type === 'admin' && (
                          <button disabled={busyId === r.registration_id} onClick={() => openAckModal(r)} style={actionBtn('#a78bfa')} title="Parent confirmed offline — acknowledge and (optionally) charge">
                            <FaHandshake size={11} /> Offline Confirm
                          </button>
                        )}
                        {r.payment_status !== 'COMPLETED' && r.registration_status !== 'VOIDED'
                          && (r.registration_status === 'APPROVED' || r.booked_by_type === 'parent') && (
                          <button disabled={busyId === r.registration_id} onClick={() => runAction('pay', r.registration_id)} style={actionBtn(getBrandColors().green)}>
                            <FaDollarSign size={10} /> Pay
                          </button>
                        )}
                        {r.registration_status !== 'VOIDED' && r.registration_status !== 'REGISTERED' && (
                          <button disabled={busyId === r.registration_id} onClick={() => openAckModal(r)} style={actionBtn('#9ca3af')} title="Add or edit internal notes">
                            <FaStickyNote size={10} /> Notes
                          </button>
                        )}
                        {r.registration_status !== 'VOIDED' && (
                          <button disabled={busyId === r.registration_id} onClick={() => runAction('void', r.registration_id)} style={actionBtn(getBrandColors().red)}>
                            <FaBan size={10} /> Void
                          </button>
                        )}
                        {refundable && (
                          <button disabled={busyId === r.registration_id} onClick={() => openRefundModal(r)} style={actionBtn('#60a5fa')}>
                            <FaUndo size={10} /> Refund
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {sortedRows.length === 0 && (
                  <tr><td colSpan={11} style={{ padding: '20px', textAlign: 'center', color: T.textMuted }}>No registrations found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {ackModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div style={{ ...glassCard, width: 460, marginBottom: 0 }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: T.textPrimary, margin: '0 0 6px', fontFamily: FONT }}>
              {ackModal.registration_status === 'REGISTERED' ? 'Offline Acknowledgement' : 'Registration Notes'}
            </h3>
            <p style={{ fontSize: '12.5px', color: T.textMuted, margin: '0 0 16px', fontFamily: FONT }}>
              {ackModal.first_name} {ackModal.last_name} · {ackModal.age_code} · Total <strong style={{ color: T.textPrimary }}>${Number(ackModal.total_cost).toFixed(2)}</strong>
            </p>
            <div style={{ marginBottom: '18px' }}>
              <label style={labelStyle}>Notes {ackModal.registration_status === 'REGISTERED' ? '(recorded with the acknowledgement)' : ''}</label>
              <textarea value={ackNotes} onChange={(e) => setAckNotes(e.target.value)} rows={3}
                placeholder="e.g. Parent confirmed availability by phone on 12 Aug; paying at the venue"
                style={{ ...inputStyle, resize: 'vertical', minHeight: 70 }} />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={() => setAckModal(null)} style={{
                padding: '9px 16px', borderRadius: '8px', border: `1px solid ${T.inputBdr}`,
                background: T.glass, color: T.inputText, fontWeight: 700, fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
              }}>Cancel</button>
              {ackModal.registration_status === 'REGISTERED' ? (
                <>
                  <button disabled={busyId === ackModal.registration_id} onClick={() => submitAck(false)} style={{
                    padding: '9px 16px', borderRadius: '8px', border: '1px solid rgba(96,165,250,0.4)',
                    background: 'rgba(96,165,250,0.12)', color: '#60a5fa', fontWeight: 700, fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
                  }}>Acknowledge</button>
                  <button disabled={busyId === ackModal.registration_id} onClick={() => submitAck(true)} style={{
                    padding: '9px 16px', borderRadius: '8px', border: 'none',
                    background: getBrandColors().green, color: getBrandColors().blueDark, fontWeight: 700, fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
                  }}>Acknowledge &amp; Pay</button>
                </>
              ) : (
                <button disabled={busyId === ackModal.registration_id} onClick={() => submitAck(false)} style={{
                  padding: '9px 18px', borderRadius: '8px', border: 'none',
                  background: getBrandColors().yellow, color: getBrandColors().blueDark, fontWeight: 700, fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
                }}>Save Notes</button>
              )}
            </div>
          </div>
        </div>
      )}

      {refundModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div style={{ ...glassCard, width: 420, marginBottom: 0 }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: T.textPrimary, margin: '0 0 6px', fontFamily: FONT }}>
              Refund Registration
            </h3>
            <p style={{ fontSize: '12.5px', color: T.textMuted, margin: '0 0 16px', fontFamily: FONT }}>
              Paid ${Number(refundModal.total_cost).toFixed(2)} · Already refunded ${Number(refundModal.refunded_amount || 0).toFixed(2)} ·
              Remaining <strong style={{ color: T.textPrimary }}>${(Number(refundModal.total_cost) - Number(refundModal.refunded_amount || 0)).toFixed(2)}</strong>
            </p>
            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Refund Type</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setRefundType('full')} style={{
                  flex: 1, padding: '9px', borderRadius: '8px', border: `2px solid ${refundType === 'full' ? getBrandColors().yellow : T.inputBdr}`,
                  background: refundType === 'full' ? 'rgba(247,206,11,0.15)' : T.glass, color: refundType === 'full' ? getBrandColors().yellow : T.textLabel,
                  fontWeight: 700, fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
                }}>Full</button>
                <button onClick={() => setRefundType('partial')} style={{
                  flex: 1, padding: '9px', borderRadius: '8px', border: `2px solid ${refundType === 'partial' ? getBrandColors().yellow : T.inputBdr}`,
                  background: refundType === 'partial' ? 'rgba(247,206,11,0.15)' : T.glass, color: refundType === 'partial' ? getBrandColors().yellow : T.textLabel,
                  fontWeight: 700, fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
                }}>Partial</button>
              </div>
            </div>
            {refundType === 'partial' && (
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Amount</label>
                <input type="number" step="0.01" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} placeholder="0.00" style={inputStyle} />
              </div>
            )}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Reason</label>
              <input type="text" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="Optional" style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setRefundModal(null)} style={{
                padding: '9px 18px', borderRadius: '8px', border: `1px solid ${T.inputBdr}`,
                background: T.glass, color: T.inputText, fontWeight: 700, fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
              }}>Cancel</button>
              <button onClick={submitRefund} style={{
                padding: '9px 18px', borderRadius: '8px', border: 'none',
                background: getBrandColors().yellow, color: getBrandColors().blueDark, fontWeight: 700, fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
              }}>Confirm Refund</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentAdminApproval;
